
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ExternalLink, Menu, Lock, LogOut, Eye, EyeOff } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useAuth } from '@/contexts/AuthContext';

interface MobileNavMenuProps {
  currentPath: string;
  onLoginClick: () => void;
}

const MobileNavMenu: React.FC<MobileNavMenuProps> = ({ currentPath, onLoginClick }) => {
  const { isAuthenticated, logout, toggleProviderView, isProviderView } = useAuth();
  
  const isMedsPage = currentPath === '/meds';
  const isResourcesPage = currentPath === '/education';
  const isProvidersPage = currentPath === '/providers';

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="sm:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[80%] sm:max-w-sm pt-12">
        <nav className="flex flex-col space-y-6 mt-6">
          <Link 
            to="/meds" 
            className={`text-base font-medium ${isMedsPage ? 'text-primary' : 'text-foreground'}`}
          >
            Medications
          </Link>
          <Link 
            to="/education" 
            className={`text-base font-medium ${isResourcesPage ? 'text-primary' : 'text-foreground'}`}
          >
            Resources
          </Link>
          <Link 
            to="/providers" 
            className={`text-base font-medium ${isProvidersPage ? 'text-primary' : 'text-foreground'}`}
          >
            Providers
          </Link>
          
          <Separator />
          
          {isAuthenticated ? (
            <>
              <Button 
                variant="outline" 
                className="w-full flex items-center justify-start"
                onClick={toggleProviderView}
              >
                {isProviderView ? <EyeOff className="mr-2 h-5 w-5" /> : <Eye className="mr-2 h-5 w-5" />}
                {isProviderView ? "Hide Clinical Details" : "Show Clinical Details"}
              </Button>
              
              <Button 
                variant="ghost" 
                className="w-full flex items-center justify-start text-muted-foreground"
                onClick={logout}
              >
                <LogOut className="mr-2 h-5 w-5" />
                Logout
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-start"
              onClick={() => {
                onLoginClick();
                // Close the sheet after a short delay
                setTimeout(() => {
                  const closeButton = document.querySelector('[data-radix-collection-item]') as HTMLElement;
                  if (closeButton) closeButton.click();
                }, 100);
              }}
            >
              <Lock className="mr-2 h-5 w-5" />
              Provider Login
            </Button>
          )}
          
          <Button 
            variant="secondary" 
            className="w-full flex items-center justify-center hover:bg-secondary/90 font-medium" 
            onClick={() => window.open('https://link.trimfit.tech/widget/form/mJSjpTm4gmTrZFDQQU1p', '_blank')}
          >
            Speak with a Provider <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavMenu;
